/*
 * Homeline search engine, shared by every search box on the site: the header
 * field (assets/hl-header.js), the mobile tab bar's search
 * (assets/hl-tabbar.js) and the catalogue's ?q= filter (assets/hl-shop.js).
 * Loaded once from layout/theme.liquid as window.HLSearchBrain.
 *
 * A search should never end on nothing, so it runs in layers:
 *   1. Shopify's predictive search (/search/suggest.json): products, query
 *      suggestions, collections and pages. Service line items (Delivery,
 *      Rubbish Removal, surcharges) are dropped: they are charges the cart
 *      adds at checkout, not things to buy on their own.
 *   2. With fewer than two products, each word is corrected against a local
 *      index of every collection, product title, type and tag, plus the
 *      synonym and intent words. Correction is deliberately generous: a word
 *      within a few typos, or one that SOUNDS the same ("rubish" → rubbish,
 *      "cowch" → couch, "dinning" → dining), is taken. The corrected query is
 *      searched again.
 *   3. Intent shortcuts from assets/search-intents.json (warranty, rubbish
 *      removal, delivery, careers, …), matched just as loosely.
 *   4. Still no products: the closest products from the local index.
 */
(function () {
  'use strict';

  var INDEX_KEY = 'hlh_search_index_v2';
  var script = document.currentScript;
  var INTENTS_URL = (script && script.dataset.intents) || '/assets/search-intents.json';

  /* ─────────────── text ─────────────── */

  // Lower-case, fold accents, drop punctuation, collapse spaces, singularise.
  function normalise(raw) {
    var s = String(raw || '').toLowerCase();
    if (s.normalize) s = s.normalize('NFD').replace(/[̀-ͯ]/g, '');
    s = s.replace(/&/g, ' and ').replace(/[^a-z0-9.\s-]/g, ' ').replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
    return s.split(' ').map(singular).join(' ');
  }

  function singular(w) {
    if (w.length > 4 && /(ss|us|is)$/.test(w)) return w;
    if (w.length > 4 && /(s|x|z|ch|sh)es$/.test(w)) return w.slice(0, -2);
    if (w.length > 3 && /[^s]s$/.test(w)) return w.slice(0, -1);
    return w;
  }

  // A rough sound-alike key: consonant skeleton after the common English
  // spelling swaps, first letter kept. "rubbish" and "rubish" → rbs;
  // "removal" and "remooval" → rmvl; "couch" and "cowch" → kx.
  function sound(w) {
    if (!w) return '';
    var s = w
      .toLowerCase()
      .replace(/[^a-z]/g, '')
      .replace(/^kn/, 'n')
      .replace(/^wr/, 'r')
      .replace(/^ps/, 's')
      .replace(/ph/g, 'f')
      .replace(/ck/g, 'k')
      .replace(/sch/g, 'sk')
      .replace(/(ch|sh|tch)/g, 'x')
      .replace(/c(?=[eiy])/g, 's')
      .replace(/c/g, 'k')
      .replace(/q/g, 'k')
      .replace(/z/g, 's')
      .replace(/dg/g, 'j')
      .replace(/gh/g, '')
      .replace(/v/g, 'f')
      .replace(/th/g, '0');
    if (!s) return '';
    var first = s.charAt(0);
    var rest = s.slice(1).replace(/[aeiouyhw]/g, '');
    var out = (/[aeiou]/.test(first) ? 'a' : first) + rest;
    return out.replace(/(.)\1+/g, '$1');
  }

  // Damerau-Levenshtein (optimal string alignment), exiting early once a
  // whole row is past `max`.
  function distance(a, b, max) {
    if (a === b) return 0;
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var prev2 = null;
    var prev = [];
    var cur;
    var i;
    var j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur = [i];
      var rowMin = i;
      for (j = 1; j <= b.length; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
          v = Math.min(v, prev2[j - 2] + 1);
        }
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      prev2 = prev;
      prev = cur;
    }
    return prev[b.length];
  }

  // How many typos a word may carry and still be recognised. Generous on
  // purpose: 1 for 3 letters, 2 for 4–5, 3 for 6–8, 4 for 9+, never more
  // than half the word.
  function allowance(len) {
    var n = len >= 9 ? 4 : len >= 6 ? 3 : len >= 4 ? 2 : len === 3 ? 1 : 0;
    return Math.min(n, Math.floor(len / 2));
  }

  function sharedPrefix(a, b) {
    var n = 0;
    while (n < a.length && n < b.length && a.charAt(n) === b.charAt(n)) n++;
    return n;
  }

  /*
    How alike a typed word is to a known one, or -1 when they are not close
    enough to call the same word. Higher is better. Sounding the same counts
    for a lot; a different first letter costs, because people rarely get the
    first letter wrong.
  */
  function likeness(w, c, soundW, strict) {
    if (w === c) return 1000;
    var len = Math.max(w.length, c.length);
    // Strict (intent cards, fallback products): judged by the shorter word,
    // one typo fewer, so "carear" is not "care" and "claim" is not "chair".
    var max = strict ? allowance(Math.min(w.length, c.length)) - 1 : allowance(w.length);
    var d = distance(w, c, Math.max(max, 3));
    // Sounding alike only counts between words of about the same length:
    // "career" and "care" share a skeleton but are different words.
    var same = !!soundW && soundW.length >= 2 && soundW === sound(c) &&
      Math.abs(w.length - c.length) <= (strict ? 1 : 2);
    // Short words only by sound, and only with the same first letter.
    if (w.length <= 3 || c.length <= 3) {
      if (!(same && w.charAt(0) === c.charAt(0))) return -1;
    } else if (!same && (d > max || w.charAt(0) !== c.charAt(0))) {
      return -1;
    }
    var score = 100 - (d / len) * 100;
    if (same) score += 40;
    if (w.charAt(0) === c.charAt(0)) score += 10;
    score += sharedPrefix(w, c) * 2;
    return score;
  }

  /* ─────────────── money ─────────────── */

  var moneyFmt = null;
  try {
    moneyFmt = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AUD'
    });
  } catch (e) {}

  function money(amount) {
    var n = Number(amount);
    if (!isFinite(n)) return '';
    var s = moneyFmt ? moneyFmt.format(n) : '$' + n.toFixed(2);
    return s.replace(/\.00$/, '').replace(/^A\$/, '$');
  }

  function isService(p) {
    var type = String(p.type || p.product_type || '').toLowerCase();
    var tags = p.tags;
    if (typeof tags === 'string') tags = tags.split(',');
    var tagged = (tags || []).some(function (t) {
      return String(t).trim().toLowerCase() === 'service';
    });
    return type === 'service' || tagged;
  }

  /* ─────────────── the engine ─────────────── */

  var synonyms = {};
  var intents = [];
  var fallback = null;
  var vocab = null;
  var vocabWords = [];
  var vocabSounds = {};
  var products = [];
  var ready = null;
  var indexReady = null;

  function load() {
    if (ready) return ready;
    ready = fetch(INTENTS_URL, { credentials: 'same-origin' })
      .then(function (r) {
        return r.ok ? r.json() : {};
      })
      .catch(function () {
        return {};
      })
      .then(function (data) {
        var syn = data.synonyms || {};
        Object.keys(syn).forEach(function (k) {
          synonyms[normalise(k)] = normalise(syn[k]);
        });
        intents = (data.intents || []).map(function (it) {
          return {
            id: it.id,
            icon: it.icon,
            title: it.title,
            description: it.description,
            url: it.url,
            keywords: (it.keywords || []).map(normalise)
          };
        });
        fallback = data.fallback || null;
      });
    return ready;
  }

  function loadIndex() {
    if (indexReady) return indexReady;

    var cached = null;
    try {
      cached = JSON.parse(sessionStorage.getItem(INDEX_KEY) || 'null');
    } catch (e) {}

    var build = cached
      ? Promise.resolve(cached)
      : Promise.all([
          fetch('/collections.json?limit=250').then(function (r) {
            return r.ok ? r.json() : { collections: [] };
          }),
          fetch('/products.json?limit=250').then(function (r) {
            return r.ok ? r.json() : { products: [] };
          })
        ])
          .then(function (res) {
            var words = {};
            var add = function (text) {
              normalise(text)
                .split(' ')
                .forEach(function (w) {
                  if (w.length >= 3 && !/^\d/.test(w)) words[w] = (words[w] || 0) + 1;
                });
            };
            (res[0].collections || []).forEach(function (c) {
              add(c.title);
            });
            var list = [];
            (res[1].products || []).forEach(function (p) {
              if (isService(p)) return;
              add(p.title);
              add(p.product_type);
              var tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',');
              tags.forEach(function (t) {
                if (String(t).indexOf(':') === -1) add(t);
                else add(String(t).split(':')[1]);
              });
              var v = (p.variants && p.variants[0]) || {};
              list.push({
                t: p.title,
                u: '/products/' + p.handle,
                ty: p.product_type || '',
                p: v.price || '',
                i: (p.images && p.images[0] && p.images[0].src) || '',
                k: normalise([p.title, p.product_type].concat(tags.map(function (t) { return String(t).replace(/^[a-z]+:/i, ''); })).join(' '))
              });
            });
            var data = { words: words, products: list };
            try {
              sessionStorage.setItem(INDEX_KEY, JSON.stringify(data));
            } catch (e) {}
            return data;
          })
          .catch(function () {
            return { words: {}, products: [] };
          });

    indexReady = Promise.all([build, load()]).then(function (res) {
      var data = res[0];
      vocab = data.words || {};
      products = data.products || [];
      var addWord = function (w) {
        if (w.length >= 3) vocab[w] = (vocab[w] || 0) + 1;
      };
      Object.keys(synonyms).forEach(function (k) {
        k.split(' ').concat(synonyms[k].split(' ')).forEach(addWord);
      });
      intents.forEach(function (it) {
        it.keywords.forEach(function (k) {
          k.split(' ').forEach(addWord);
        });
      });
      vocabWords = Object.keys(vocab);
      vocabSounds = {};
      vocabWords.forEach(function (w) {
        vocabSounds[w] = sound(w);
      });
    });
    return indexReady;
  }

  // The closest known word, or the word itself when nothing is close.
  function closest(w) {
    if (!w || !vocab || vocab[w] || /\d/.test(w) || w.length < 3) return w;
    var sw = sound(w);
    var best = null;
    var bestScore = -1;
    for (var i = 0; i < vocabWords.length; i++) {
      var c = vocabWords[i];
      var sc = likeness(w, c, sw);
      if (sc < 0) continue;
      sc += Math.min(10, vocab[c]);
      if (sc > bestScore) {
        best = c;
        bestScore = sc;
      }
    }
    return best || w;
  }

  function fixTypos(norm) {
    return norm.split(' ').map(closest).join(' ');
  }

  // Phrase synonyms first (so "tv stand" wins over "stand"), then words.
  function expand(norm) {
    var out = ' ' + norm + ' ';
    Object.keys(synonyms)
      .sort(function (a, b) {
        return b.length - a.length;
      })
      .forEach(function (k) {
        out = out.split(' ' + k + ' ').join(' ' + synonyms[k] + ' ');
      });
    // "bedside table" expanded from "bedside" must not read "table table".
    return out
      .trim()
      .split(/\s+/)
      .filter(function (w, i, all) {
        return w !== all[i - 1];
      })
      .join(' ');
  }

  // Does a typed word loosely match a keyword word? `strict` (for intent
  // cards) allows one typo fewer, so a product name rarely trips a card.
  function wordMatches(t, k, strict) {
    if (t === k) return true;
    if (t.length < 3 || k.length < 3) return false;
    // A prefix the visitor is still typing: "warr" → warranty.
    if (t.length >= 4 && k.indexOf(t) === 0) return true;
    return likeness(t, k, sound(t), strict) >= 0;
  }

  function matchIntents(norm, limit) {
    if (!norm) return [];
    var tokens = norm.split(' ');
    var hits = [];
    intents.forEach(function (it) {
      var best = 0;
      it.keywords.forEach(function (k) {
        if (!k) return;
        var kw = k.split(' ');
        // Every word of the keyword has to be there, in any spelling.
        var all = kw.every(function (part) {
          return tokens.some(function (t) {
            return wordMatches(t, part, true);
          });
        });
        if (all) best = Math.max(best, kw.length * 10 + k.length);
      });
      if (best) hits.push({ it: it, score: best });
    });
    hits.sort(function (a, b) {
      return b.score - a.score;
    });
    return hits.slice(0, limit || 3).map(function (h) {
      return h.it;
    });
  }

  // Last resort: products from the local index whose words loosely match
  // every typed word.
  function localProducts(norm, limit) {
    var tokens = norm.split(' ').filter(function (t) {
      return t.length >= 2;
    });
    if (!tokens.length) return [];
    var scored = [];
    products.forEach(function (p) {
      var words = p.k.split(' ');
      var hit = 0;
      tokens.forEach(function (t) {
        if (p.k.indexOf(t) !== -1 || words.some(function (w) { return wordMatches(t, w, true); })) hit++;
      });
      if (hit) scored.push({ p: p, hit: hit });
    });
    scored.sort(function (a, b) {
      return b.hit - a.hit;
    });
    var top = scored.length ? scored[0].hit : 0;
    return scored
      .filter(function (s) {
        return s.hit === top;
      })
      .slice(0, limit || 4)
      .map(function (s) {
        return { title: s.p.t, url: s.p.u, type: s.p.ty, price: money(s.p.p), image: s.p.i };
      });
  }

  function suggest(q, opts) {
    var base = (opts && opts.suggestUrl) || '/search/suggest';
    var url =
      base +
      '.json?q=' +
      encodeURIComponent(q) +
      '&resources[type]=product,query,collection,page' +
      '&resources[limit]=' + ((opts && opts.limit) || 8) +
      '&resources[options][unavailable_products]=last';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (json) {
        var res = (json && json.resources && json.resources.results) || {};
        return {
          products: (res.products || [])
            .filter(function (p) {
              return !isService(p);
            })
            .map(function (p) {
              return {
                title: p.title,
                url: p.url,
                type: p.type || p.product_type || '',
                price: money(p.price),
                image: p.image || (p.featured_image && p.featured_image.url) || ''
              };
            }),
          queries: (res.queries || []).map(function (s) {
            return { text: s.text, url: s.url };
          }),
          collections: (res.collections || []).map(function (c) {
            return { text: c.title, url: c.url };
          }),
          pages: (res.pages || []).map(function (pg) {
            return { title: pg.title, url: pg.url };
          })
        };
      })
      .catch(function () {
        return { products: [], queries: [], collections: [], pages: [] };
      });
  }

  /*
    The whole pipeline. Resolves to:
      { q, products, suggestions, pages, intents, corrected, typo, local, nothing }
    `corrected` is the query actually searched when it differs from what was
    typed; `typo` says a spelling fix (not just a synonym) produced it.
  */
  function search(raw, opts) {
    opts = opts || {};
    var q = String(raw || '').trim();
    var norm = normalise(q);
    if (!q) return Promise.resolve(null);

    return Promise.all([suggest(q, opts), load()])
      .then(function (res) {
        var first = res[0];
        var hits = matchIntents(norm, opts.intents);
        if (first.products.length >= 2 || opts.noCorrect) {
          return { res: first, intents: hits, corrected: null, typo: false, fixed: norm };
        }
        return loadIndex().then(function () {
          var fixed = fixTypos(norm);
          var typo = fixed !== norm;
          var expanded = expand(fixed);
          // Intents get every chance: the typed words, the corrected words.
          matchIntents(fixed, opts.intents).forEach(function (it) {
            if (hits.indexOf(it) === -1) hits.push(it);
          });
          hits = hits.slice(0, opts.intents || 3);
          if (expanded === norm) {
            return { res: first, intents: hits, corrected: null, typo: false, fixed: norm };
          }
          return suggest(expanded, opts).then(function (second) {
            var better =
              second.products.length > first.products.length ||
              (!first.queries.length && !first.collections.length && !first.pages.length &&
                (second.queries.length || second.collections.length || second.pages.length));
            if (!better) return { res: first, intents: hits, corrected: null, typo: false, fixed: expanded };
            return { res: second, intents: hits, corrected: expanded, typo: typo, fixed: expanded };
          });
        });
      })
      .then(function (out) {
        var r = out.res;
        var found = r.products.slice();
        var local = false;
        // A search that hit a shortcut card (rubbish removal, warranty…) is
        // about a service, not a piece, so no guessed products under it.
        if (!found.length && !out.intents.length) {
          return loadIndex().then(function () {
            found = localProducts(out.fixed, opts.limit || 6);
            local = found.length > 0;
            return finish(found, local);
          });
        }
        return finish(found, local);

        function finish(list, isLocal) {
          var seen = {};
          var suggestions = r.queries
            .concat(r.collections)
            .filter(function (s) {
              var k = (s.text || '').toLowerCase();
              if (!k || seen[k]) return false;
              seen[k] = true;
              return true;
            })
            .slice(0, 5);
          // A page an intent card already links to is not listed twice.
          var linked = {};
          out.intents.forEach(function (it) {
            linked[it.url] = true;
          });
          var pages = r.pages.filter(function (pg) {
            return !linked[pg.url];
          }).slice(0, 3);
          return {
            q: q,
            products: list,
            suggestions: suggestions,
            pages: pages,
            intents: out.intents,
            corrected: out.corrected,
            typo: out.typo,
            local: isLocal,
            nothing: !list.length && !suggestions.length && !pages.length && !out.intents.length
          };
        }
      });
  }

  // Loose word match for a page that filters what it already has (the
  // catalogue's ?q=). Each typed word, or a synonym it sounds like ("cowch"
  // → couch → sofa), must loosely match a word in `hay`.
  function looseMatch(q, hay) {
    var words = hay.split(/\s+/).map(singular);
    var keys = Object.keys(synonyms);
    var hit = function (t) {
      if (hay.indexOf(t) !== -1) return true;
      return words.some(function (w) {
        return wordMatches(t, w, true);
      });
    };
    return normalise(q)
      .split(' ')
      .filter(Boolean)
      .every(function (t) {
        if (hit(t)) return true;
        return keys.some(function (k) {
          if (k.indexOf(' ') !== -1 || !wordMatches(t, k)) return false;
          return synonyms[k].split(' ').every(hit);
        });
      });
  }

  window.HLSearchBrain = {
    normalise: normalise,
    sound: sound,
    distance: distance,
    money: money,
    isService: isService,
    load: load,
    loadIndex: loadIndex,
    fixTypos: fixTypos,
    expand: expand,
    matchIntents: matchIntents,
    localProducts: localProducts,
    looseMatch: looseMatch,
    loaded: function () {
      return intents.length > 0 || Object.keys(synonyms).length > 0;
    },
    search: search,
    fallback: function () {
      return fallback;
    }
  };
})();
